import React, { useEffect, useState } from 'react';
import Ghost from './Ghost.jsx';
import './auth-modal.css';
import { completeWebLogin } from '../../services/webOidc';
import { persistTokens } from '../../services/tokenRefresher';
import { adoptRefreshToken } from '../../services/webSession';

// ONE exchange per page load. React StrictMode runs effects twice in
// development, and the sign-in code + verifier are single-use: a second run
// would find the verifier already consumed and show a false "expired" error
// while the first run is signing the user in.
let exchange = null;
function runExchangeOnce(href) {
  // The refresh token goes straight into the server's HttpOnly cookie (card
  // 2.3); only the short-lived access token comes back to the page.
  if (!exchange) {
    exchange = completeWebLogin(href).then(async (tokens) => {
      if (!tokens.refreshToken) return tokens;
      try {
        return await adoptRefreshToken(tokens.refreshToken);
      } catch (first) {
        // The sign-in itself already succeeded and the code is spent — a blip
        // on the adopt call must not throw that away and send the user back
        // through the whole flow. One retry, then fall back to the access
        // token alone so the page is usable until the next refresh.
        try {
          return await adoptRefreshToken(tokens.refreshToken);
        } catch (second) {
          console.warn('Could not store the web session cookie:', second?.message || second);
          return { ...tokens, refreshToken: null };
        }
      }
    });
  }
  return exchange;
}

/** Test hook: forget the in-flight exchange between test cases. */
export function resetWebAuthCallbackForTests() {
  exchange = null;
}

/**
 * Web app: the page auth.apiboo.com redirects back to (/auth/callback).
 *
 * Exchanges the code, stores the tokens exactly as the desktop sign-in does,
 * and replaces the URL with "/" so the code never stays in history. The app
 * then starts normally and initializeAuth loads the signed-in user.
 */
export default function WebAuthCallback({ href = window.location.href, onDone = () => window.location.replace('/') }) {
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    runExchangeOnce(href)
      .then((tokens) => {
        if (cancelled) return;
        persistTokens(tokens);
        onDone();
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Sign-in failed. Please try again.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="apiboo-auth-scrim apiboo-auth-scrim--page" data-testid="web-auth-callback">
      <div className="modal" style={{ '--modal-w': '380px' }} role="dialog" aria-modal="true" aria-label="Signing in">
        <div className="m-head">
          <div className="m-logo">
            <Ghost variant="classic" size={44} animated glow />
          </div>
          <div className="m-ttl">{error ? 'Sign-in did not finish' : 'Signing you in…'}</div>
        </div>
        <div className="m-body">
          {error ? (
            <>
              <div className="err-msg" style={{ marginTop: 0, marginBottom: 14 }}>
                {error}
              </div>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => window.location.replace('/')}>
                Back to Apiboo
              </button>
            </>
          ) : (
            <p style={{ margin: 0, fontSize: 13.5, color: 'var(--fg-2)', textAlign: 'center' }}>One moment…</p>
          )}
        </div>
      </div>
    </div>
  );
}
